import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Info, Languages, LogOut, Monitor, Moon, Sun, SunMoon, UserRound } from 'lucide-react';
import { LOCALES, type Locale } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { useCurrentUser } from '@/features/auth/auth.store';
import { useSignOut } from '@/features/auth/useSession';
import { useUiStore, type ThemePreference } from '@/shared/theme/ui.store';
import {
  Avatar,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui';
import { ROLE_TONE } from './DemoSwitcher';
import { AboutDialog } from '@/features/app-update/AboutDialog';
import { appBuildTitle, appVersionLabel } from '@/shared/lib/app-version';

const LOCALE_LABEL: Record<Locale, string> = { pl: 'Polski', en: 'English' };

export function UserMenu() {
  const { t, locale, setLocale } = useI18n();
  const user = useCurrentUser();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [aboutOpen, setAboutOpen] = useState(false);
  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t('topbar.userMenu')}
          className="flex h-11 items-center gap-2.5 rounded-xl px-1.5 transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 xl:pr-2.5"
        >
          <span
            aria-hidden
            className="brand-gradient flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          >
            {(user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')}
          </span>
          <span className="hidden text-left leading-tight xl:block">
            <span className="block max-w-[10rem] truncate text-[13px] font-semibold text-foreground">
              {user.firstName} {user.lastName}
            </span>
            <span className="block text-2xs text-sidebar-foreground">{t(`roles.${user.role}`)}</span>
          </span>
          <ChevronDown className="hidden size-4 text-sidebar-foreground xl:block" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <Avatar firstName={user.firstName} lastName={user.lastName} size="md" />
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium">
                {user.firstName} {user.lastName}
              </p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 px-2 pb-2">
            <Badge tone={ROLE_TONE[user.role]} dot>
              {t(`roles.${user.role}`)}
            </Badge>
            <Badge>{t(`dataScope.${user.dataScope}`)}</Badge>
          </div>
          <DropdownMenuItem onSelect={() => navigate('/account')}>
            <UserRound />
            {t('acct.menu')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Languages />
              {t('topbar.language')}
              <span className="ml-auto text-xs uppercase text-muted-foreground">{locale}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={locale} onValueChange={(v) => setLocale(v as Locale)}>
                {LOCALES.map((l) => (
                  <DropdownMenuRadioItem key={l} value={l}>
                    {LOCALE_LABEL[l]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SunMoon />
              {t('topbar.theme')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemePreference)}>
                <DropdownMenuRadioItem value="light">
                  <Sun /> {t('topbar.themeLight')}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">
                  <Moon /> {t('topbar.themeDark')}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">
                  <Monitor /> {t('topbar.themeSystem')}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setAboutOpen(true)} title={appBuildTitle()}>
            <Info />
            {t('appu.about.open')}
            <span className="ml-auto text-xs tabular-nums text-muted-foreground">{appVersionLabel()}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={() => void signOut('logout')}>
            <LogOut />
            {t('common.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </>
  );
}
